"use client"

import { useState } from "react"
import {
  Search, Clock, CheckCircle, User, MessageSquare,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  AlertCircleIcon,
} from "lucide-react"
import { get } from "../utility/fetch"

export default function TrackFeedback() {
  const [trackingId, setTrackingId] = useState("")
  const [feedback, setFeedback] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!trackingId.trim()) {
      setError("Please enter a tracking ID")
      return
    }

    setLoading(true)
    setError("")
    setFeedback(null)

    try {
      const res = await get(`/api/customerengagement/track-id${trackingId}`)
      const result = await res

      if (result.isSuccess && result.data) {
        setFeedback(result.data)
      } else {
        setError("Feedback not found. Please check your tracking ID and try again.")
      }
    } catch (err) {
      setError("An error occurred. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <Clock className="w-4 h-4" />
      case "in-progress":
        return <AlertCircleIcon className="w-4 h-4" />
      case "resolved":
        return <CheckCircle className="w-4 h-4" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "in-progress":
        return "bg-blue-100 text-blue-800"
      case "resolved":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Track Feedback</h1>
        <p className="text-gray-600 mt-1">Enter your tracking ID to check the status of your feedback</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="trackingId" className="block text-sm font-medium text-gray-700 mb-2">
              Tracking ID
            </label>
            <input
              type="text"
              id="trackingId"
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., TCK202506110911233NHC0QZPG"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              {loading ? "Searching..." : "Track"}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}
      </div>

      {feedback && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Feedback Details</h2>
            <p className="text-sm text-gray-600">Tracking ID: {feedback.trackingId}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Type</p>
              <p className="text-gray-900">{feedback.type}</p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                  feedback.status
                )}`}
              >
                {getStatusIcon(feedback.status)}
                <span className="ml-1 capitalize">{feedback.status}</span>
              </span>
            </div>
            <div>
              <p className="text-gray-500">Location</p>
              <p className="text-gray-900">{feedback.location}</p>
            </div>
            <div>
              <p className="text-gray-500">Contact Email</p>
              <p className="text-gray-900">{feedback.contactEmail}</p>
            </div>
            <div>
              <p className="text-gray-500">Created At</p>
              <p className="text-gray-900">{formatDate(feedback.createdAt)}</p>
            </div>
            {feedback.actionTaken && (
              <div className="md:col-span-2">
                <p className="text-gray-500">Action Taken</p>
                <p className="text-gray-900">{feedback.actionTaken}</p>
              </div>
            )}
            {feedback.resolutionNotes && (
              <div className="md:col-span-2">
                <p className="text-gray-500">Resolution Notes</p>
                <p className="text-gray-900">{feedback.resolutionNotes}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
